import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { t } from '@reos/i18n';
import { isNavItemVisible } from '../lib/capabilities.js';
import { SHELL_MENU_ITEMS } from '../lib/nav.js';
import { CASE_SEARCH_MIN_LENGTH, searchCasesByName } from '../lib/case-address-lookup.js';
import { listParties, type PartyDto } from '../lib/parties-api.js';
import { useSession } from '../stores/session.js';
import { useNavigate } from '@tanstack/react-router';

/**
 * Prohledatelne polozky = presne to, co vidi sidebar (BLOK 2.5, zadna
 * duplicitni definice menu). Krok UXA1: `MENU_ITEMS` uz nenese preparing
 * ani `hiddenFromMenu` polozky (napr. „Elektronické podpisy" — ty maji
 * svuj vlastni vstupni bod, viz `nav.ts`).
 */
/**
 * Krok QAFIX3 (nalez N06): `SHELL_MENU_ITEMS` nese i vstupy podle opravneni
 * („Objednávky reportů"). Paleta tim hleda PRESNE to, co kresli panel —
 * polozka, kterou vidi menu a ne Ctrl+K, by byla druhy seznam.
 */
const ALL_NAV_ITEMS = SHELL_MENU_ITEMS;

/**
 * Krok APP2 bod 2 (bod 21 zadani) — CTRL+K HLEDA I V ZAKAZKACH A KLIENTECH.
 *
 * Do APP2 prohledavala paleta jen MENU, takze „Nymburk" (adresa zakazky)
 * vracelo „Nic nenalezeno" — makler z klavesnice nenasel to, kvuli cemu
 * aplikaci otevrel. Hleda SERVER, ne prohlizec:
 *  - zakazky `GET /v1/cases?q=` pres SSOT `searchCasesByName` (krok PROP3B),
 *    tedy TATAZ cesta i tytez nazvy jako nabidka zakazky u faktury,
 *  - klienty `GET /v1/parties?q=` (hleda i podle normalizovaneho kontaktu).
 *
 * IZOLACE KANCELARE JE NA SERVERU: oba dotazy jdou pres RLS tenanta a seznam
 * zakazek navic pres dosah maklere (`brokerOfficeListScope`). Prohlizec tu
 * nema zadny seznam, ve kterem by mohl hledat „omylem" napric kancelarmi.
 */
const RESULT_LIMIT = 8;
/** Prodleva, nez se hledani zepta serveru (psani je rychlejsi nez sit). */
const SEARCH_DEBOUNCE_MS = 250;

interface FoundCase {
  readonly id: string;
  readonly name: string;
}

export function CommandPalette(): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [cases, setCases] = React.useState<readonly FoundCase[]>([]);
  const [clients, setClients] = React.useState<readonly PartyDto[]>([]);
  const user = useSession((s) => s.user);
  const roles = user?.roles ?? [];
  const navigate = useNavigate();

  React.useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const needle = query.trim();

  React.useEffect(() => {
    if (!open || needle.length < CASE_SEARCH_MIN_LENGTH) {
      setCases([]);
      setClients([]);
      return undefined;
    }
    let alive = true;
    const timer = window.setTimeout(() => {
      // Selhani hledani NESMI shodit paletu: menu zustava prohledatelne
      // i bez site (prazdny vysledek, zadna chyba na obrazovce).
      void searchCasesByName(needle).then(
        (found) => {
          if (alive) setCases([...found].slice(0, RESULT_LIMIT).map(([id, name]) => ({ id, name })));
        },
        () => {
          if (alive) setCases([]);
        },
      );
      void listParties({ q: needle, limit: RESULT_LIMIT }).then(
        (page) => {
          if (alive) setClients(page.items);
        },
        () => {
          if (alive) setClients([]);
        },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [open, needle]);

  const items = ALL_NAV_ITEMS.filter((i) => isNavItemVisible(roles, i)).filter((i) =>
    query ? i.label.toLowerCase().includes(query.toLowerCase()) : true,
  );
  const nicNenalezeno = items.length === 0 && cases.length === 0 && clients.length === 0;

  // Krok UXA1 (R.B. 1. 10. „jedno vyhledávání, pole = Ctrl+K"): zadne
  // viditelne druhe tlacitko „Hledat… (Ctrl+K)" vedle vyhledavaciho pole
  // v hlavicce — paletu otevira jen klavesova zkratka (listener vyse).
  // `Dialog.Root` je uz ovladany stavem `open`, Trigger nepotrebuje.
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />
        <Dialog.Content
          aria-label={t('webAdmin.commandPalette.paletteAriaLabel', { locale: 'cs' })}
          className="fixed top-20 left-1/2 -translate-x-1/2 bg-rh-card rounded-lg shadow-xl p-4 w-full max-w-lg"
        >
          <Dialog.Title className="sr-only">{t('webAdmin.commandPalette.paletteAriaLabel', { locale: 'cs' })}</Dialog.Title>
          <label htmlFor="palette-input" className="sr-only">{t('webAdmin.commandPalette.searchInMenuLabel', { locale: 'cs' })}</label>
          <input
            id="palette-input"
            autoFocus
            placeholder={t('webAdmin.commandPalette.placeholder', { locale: 'cs' })}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border rounded px-3 py-2"
            data-testid="command-input"
          />
          <ul role="listbox" className="mt-3 space-y-1">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="w-full text-left px-3 py-2 rounded hover:bg-neutral-100"
                  onClick={() => {
                    setOpen(false);
                    void navigate({ to: it.path });
                  }}
                  data-testid={`palette-item-${it.id}`}
                >
                  {it.label}
                </button>
              </li>
            ))}
            {cases.length > 0 ? (
              <li className="px-3 pt-2 text-xs text-neutral-500" data-testid="palette-group-cases">
                {t('webAdmin.commandPalette.groupCases', { locale: 'cs' })}
              </li>
            ) : null}
            {cases.map((found) => (
              <li key={found.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="w-full text-left px-3 py-2 rounded hover:bg-neutral-100"
                  onClick={() => {
                    setOpen(false);
                    void navigate({ to: '/zakazky/$id', params: { id: found.id } });
                  }}
                  data-testid={`palette-case-${found.id}`}
                >
                  {found.name}
                </button>
              </li>
            ))}
            {clients.length > 0 ? (
              <li className="px-3 pt-2 text-xs text-neutral-500" data-testid="palette-group-clients">
                {t('webAdmin.commandPalette.groupClients', { locale: 'cs' })}
              </li>
            ) : null}
            {clients.map((party) => (
              <li key={party.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="w-full text-left px-3 py-2 rounded hover:bg-neutral-100"
                  onClick={() => {
                    setOpen(false);
                    void navigate({ to: '/klienti', search: { klient: party.id } });
                  }}
                  data-testid={`palette-client-${party.id}`}
                >
                  {party.displayName}
                </button>
              </li>
            ))}
            {nicNenalezeno ? (
              <li className="text-sm text-neutral-500 px-3 py-2">{t('webAdmin.commandPalette.noResults', { locale: 'cs' })}</li>
            ) : null}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
